import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { UI } from "@wwimmo/ui";
import { useSelectionListKeyHandler } from "src/hooks/ticket/selection-list-key-handler/useSelectionListKeyHandler";
import { boldenMatchedSubstring } from "src/utils/Common";
import { IUserToAssign } from "src/stores/krediflow/AssignInvoiceStore";
import { FallbackWorkflowUserRole } from "src/stores/KrediFlowConfigurationStore";
import styles from "./FallbackWorkflowUserField.module.css";

interface IFallbackWorkflowUserFieldProps {
    field: FallbackWorkflowUserRole;
}

const FallbackWorkflowUserFieldBase = (props: IFallbackWorkflowUserFieldProps) => {
    const { t } = useTranslation();
    const { krediFlowConfigurationStore, assignInvoiceStore } = useContext(RootStoreContext);
    const { field } = props;

    const [searchQuery, setSearchQuery] = useState("");
    const [displayList, setDisplayList] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const displayName = krediFlowConfigurationStore.fallbackWorkflowUserDisplay[field];

    useEffect(() => {
        setSearchQuery(displayName);
    }, [displayName]);

    const filteredUsers = useCallback((): IUserToAssign[] => {
        const users = assignInvoiceStore.usersToAssignData;

        if (searchQuery === "" || searchQuery === displayName) {
            return users;
        }

        return users.filter((user) => user.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [searchQuery, displayName, assignInvoiceStore.usersToAssignData]);

    const onSelectUser = useCallback(
        (user: IUserToAssign) => {
            krediFlowConfigurationStore.setFallbackWorkflowUser(field, user);
            setSearchQuery(user.name);
            setDisplayList(false);
        },
        [krediFlowConfigurationStore, field]
    );

    const onSelectItem = useCallback(
        (activeListItemIndex: number) => {
            const users = filteredUsers();
            if (users[activeListItemIndex]) {
                onSelectUser(users[activeListItemIndex]);
            }
        },
        [filteredUsers, onSelectUser]
    );

    const resetSearch = useCallback(() => {
        setSearchQuery(displayName);
    }, [displayName]);

    const { onKeyDownFunction, setActiveListItemIndex, NO_LIST_ITEM_SELECTED } = useSelectionListKeyHandler({
        listName: "fallback-workflow-user",
        totalNumberOfDisplayedItems: filteredUsers().length,
        setIsListDisplayed: setDisplayList,
        isListDisplayed: displayList,
        onSelectItem: onSelectItem,
        resetSearchQuery: resetSearch
    });

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            if (!displayList || !inputRef.current) return;

            const clickedInsideInput = inputRef.current.contains(target);
            const clickedList =
                target.classList.contains("dropdown-list") && target.classList.contains("fallback-workflow-user");
            const clickedListItem =
                target.classList.contains("list-item") && target.classList.contains("fallback-workflow-user");
            const clickedHighlightedText =
                target.classList.contains("highlighted-text") &&
                target.classList.contains("fallback-workflow-user");
            const clickedListItemChild =
                target.parentElement?.classList.contains("list-item") &&
                target.parentElement.classList.contains("fallback-workflow-user");

            if (!(clickedInsideInput || clickedList || clickedListItem || clickedHighlightedText || clickedListItemChild)) {
                setDisplayList(false);
                if (searchQuery === "") {
                    krediFlowConfigurationStore.clearFallbackWorkflowUser(field);
                } else {
                    resetSearch();
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [displayList, searchQuery, resetSearch, krediFlowConfigurationStore, field]);

    const onChangeInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }, []);

    const onFocusInput = useCallback(() => {
        if (krediFlowConfigurationStore.fallbackWorkflowUser[field] && inputRef.current) {
            inputRef.current.select();
        }
        setDisplayList(true);
        setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
    }, [krediFlowConfigurationStore, field, setActiveListItemIndex, NO_LIST_ITEM_SELECTED]);

    const onClickItem = useCallback(
        (user: IUserToAssign) => () => {
            onSelectUser(user);
            setActiveListItemIndex(NO_LIST_ITEM_SELECTED);
        },
        [onSelectUser, setActiveListItemIndex, NO_LIST_ITEM_SELECTED]
    );

    const userSelectionList = (
        <ul
            id={`fallback-workflow-user-dropdown-list-${field}`}
            className={`dropdown-list fallback-workflow-user krediflow ${styles.DropdownList}`}
        >
            {filteredUsers().map((user, index) => {
                const formattedName = boldenMatchedSubstring(
                    searchQuery === displayName ? "" : searchQuery,
                    user.name,
                    "highlighted-text fallback-workflow-user"
                );

                return (
                    <li
                        key={user.userid}
                        className="d-flex align-items-center list-item fallback-workflow-user"
                        onClick={onClickItem(user)}
                        id={`fallback-workflow-user-list-item-${index}`}
                    >
                        <div
                            className={styles.UserText}
                            dangerouslySetInnerHTML={{ __html: formattedName }}
                        />
                    </li>
                );
            })}
        </ul>
    );

    return (
        <div className={styles.FieldContainer}>
            <UI.Input
                ref={inputRef}
                autoComplete="off"
                placeholder={t(
                    `screens.communication_settings.krediflow.assignment_and_visa.alternative.${field}`
                ).toString()}
                type="text"
                value={searchQuery}
                onChange={onChangeInput}
                onFocus={onFocusInput}
                onKeyDown={onKeyDownFunction}
                iconRight={<UI.Icon icon={UI.SVGIcon.Search} color="grey" size={"small"} style={{ right: "5px" }} />}
            />
            {displayList ? userSelectionList : undefined}
        </div>
    );
};

export const FallbackWorkflowUserField = Sentry.withProfiler(observer(FallbackWorkflowUserFieldBase));
